import type { GameReport, ReportPlayer, ReportQuestion, QuestionType } from '../../shared/protocol';
import { BRAND } from '../brand';

/**
 * Informe descargable de una partida.
 *
 * Dos formatos: CSV (una fila por jugador y pregunta, para Excel o Sheets) e
 * HTML autónomo con estadísticas, que se abre en cualquier navegador y se
 * imprime a PDF sin depender de la app.
 */

const TYPE_LABEL: Record<QuestionType, string> = {
  multiple_choice: 'Opción múltiple',
  true_false: 'Verdadero/Falso',
  multi_select: 'Selección múltiple',
  fill_blank: 'Completar',
  match_pairs: 'Emparejar',
};

const seconds = (ms: number) => (ms / 1000).toFixed(1);
const pct = (n: number) => `${Math.round(n * 100)}%`;
const rate = (q: ReportQuestion) => (q.totalPlayers > 0 ? q.correctCount / q.totalPlayers : 0);

function slug(s: string): string {
  return (
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^\w]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase()
      .slice(0, 40) || 'partida'
  );
}

export function reportFileName(report: GameReport, ext: 'csv' | 'html'): string {
  const date = new Date(report.finishedAt).toISOString().slice(0, 10);
  const who = report.scope === 'self' ? `-${slug(report.players[0]?.displayName ?? '')}` : '';
  return `${slug(report.quizTitle)}-${date}${who}.${ext}`;
}

export function downloadFile(name: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ------------------------------------------------------------------ *
 * CSV
 * ------------------------------------------------------------------ */

function csvCell(v: string | number): string {
  const s = String(v);
  // Una celda que empieza por = + - @ la interpretaría Excel como fórmula.
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function reportToCsv(report: GameReport): string {
  const header = [
    'Puesto',
    'Jugador',
    'Puntaje total',
    'N.º pregunta',
    'Pregunta',
    'Tipo',
    'Respuesta dada',
    'Respuesta correcta',
    'Resultado',
    'Crédito',
    'Puntos',
    'Tiempo (s)',
    'Acierto de la sala',
  ];
  const rows: (string | number)[][] = [header];

  for (const p of report.players) {
    for (const a of p.answers) {
      const q = report.questions[a.questionIndex];
      if (!q) continue;
      rows.push([
        p.placement,
        p.displayName,
        p.score,
        q.index + 1,
        q.title,
        TYPE_LABEL[q.type],
        a.answered ? a.answer : '(sin responder)',
        q.correctAnswer,
        !a.answered ? 'Sin responder' : a.isCorrect ? (a.correctness < 1 ? 'Parcial' : 'Correcta') : 'Incorrecta',
        pct(a.correctness),
        a.points,
        a.answered ? seconds(a.responseTimeMs) : '',
        pct(rate(q)),
      ]);
    }
  }

  // BOM para que Excel reconozca tildes y eñes.
  return '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

/* ------------------------------------------------------------------ *
 * HTML
 * ------------------------------------------------------------------ */

function esc(s: string | number): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** La respuesta equivocada que más se repitió en una pregunta. */
function commonMistake(report: GameReport, q: ReportQuestion): { text: string; count: number } | null {
  const counts = new Map<string, number>();
  for (const p of report.players) {
    const a = p.answers[q.index];
    if (!a?.answered || a.isCorrect || !a.answer) continue;
    counts.set(a.answer, (counts.get(a.answer) ?? 0) + 1);
  }
  let best: { text: string; count: number } | null = null;
  for (const [text, count] of counts) if (!best || count > best.count) best = { text, count };
  return best;
}

function statusCell(a: ReportPlayer['answers'][number]): string {
  if (!a.answered) return '<span class="tag none">—</span>';
  if (!a.isCorrect) return '<span class="tag bad">✗</span>';
  if (a.correctness < 1) return `<span class="tag part">${pct(a.correctness)}</span>`;
  return '<span class="tag ok">✓</span>';
}

function playerSection(report: GameReport, p: ReportPlayer): string {
  const unanswered = p.answers.filter((a) => !a.answered).length;
  const wrong = p.answers.filter((a) => a.answered && !a.isCorrect).length;
  const answeredTimes = p.answers.filter((a) => a.answered).map((a) => a.responseTimeMs);
  const fastest = answeredTimes.length ? Math.min(...answeredTimes) : 0;

  const rows = p.answers
    .map((a) => {
      const q = report.questions[a.questionIndex];
      if (!q) return '';
      const cls = !a.answered ? 'row-none' : a.isCorrect ? (a.correctness < 1 ? 'row-part' : 'row-ok') : 'row-bad';
      return `<tr class="${cls}">
        <td class="num">${q.index + 1}</td>
        <td>${esc(q.title)}</td>
        <td>${a.answered ? esc(a.answer) : '<em>Sin responder</em>'}</td>
        <td>${esc(q.correctAnswer)}</td>
        <td class="c">${statusCell(a)}</td>
        <td class="num">${a.points}</td>
        <td class="num">${a.answered ? seconds(a.responseTimeMs) + ' s' : '—'}</td>
        <td class="num muted">${pct(rate(q))}</td>
      </tr>`;
    })
    .join('');

  return `<section class="player">
    <h3><span class="place">#${p.placement}</span> ${esc(p.displayName)}</h3>
    <div class="kpis">
      ${kpi('Puntos', p.score.toLocaleString('es'))}
      ${kpi('Puesto', `${p.placement} de ${report.totalPlayers}`)}
      ${kpi('Aciertos', `${p.correctCount}/${report.questions.length}`)}
      ${kpi('Precisión', pct(p.accuracy))}
      ${kpi('Fallos', String(wrong))}
      ${kpi('Sin responder', String(unanswered))}
      ${kpi('Tiempo medio', p.answeredCount ? `${seconds(p.avgResponseMs)} s` : '—')}
      ${kpi('Más rápida', answeredTimes.length ? `${seconds(fastest)} s` : '—')}
      ${kpi('Mejor racha', String(p.bestStreak))}
    </div>
    <table>
      <thead><tr><th>#</th><th>Pregunta</th><th>Su respuesta</th><th>Correcta</th><th>Resultado</th><th>Puntos</th><th>Tiempo</th><th>Sala</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
}

function kpi(label: string, value: string): string {
  return `<div class="kpi"><div class="kl">${esc(label)}</div><div class="kv">${esc(value)}</div></div>`;
}

export function reportToHtml(report: GameReport): string {
  const full = report.scope === 'full';
  const players = report.players;
  const qs = report.questions;
  const date = new Date(report.finishedAt).toLocaleString('es');

  const avgScore = players.length ? players.reduce((s, p) => s + p.score, 0) / players.length : 0;
  const avgAccuracy = players.length ? players.reduce((s, p) => s + p.accuracy, 0) / players.length : 0;
  const sorted = [...qs].sort((a, b) => rate(a) - rate(b));
  const hardest = sorted[0];
  const easiest = sorted[sorted.length - 1];
  const withTime = players.filter((p) => p.answeredCount > 0);
  const fastest = withTime.length ? withTime.reduce((a, b) => (b.avgResponseMs < a.avgResponseMs ? b : a)) : null;

  const summary = full
    ? `<div class="kpis">
        ${kpi('Jugadores', String(report.totalPlayers))}
        ${kpi('Preguntas', String(qs.length))}
        ${kpi('Puntaje medio', Math.round(avgScore).toLocaleString('es'))}
        ${kpi('Precisión media', pct(avgAccuracy))}
        ${kpi('Ganador', players[0]?.displayName ?? '—')}
        ${kpi('Más rápido (media)', fastest ? `${fastest.displayName} · ${seconds(fastest.avgResponseMs)} s` : '—')}
        ${kpi('Pregunta más difícil', hardest ? `#${hardest.index + 1} · ${pct(rate(hardest))}` : '—')}
        ${kpi('Pregunta más fácil', easiest ? `#${easiest.index + 1} · ${pct(rate(easiest))}` : '—')}
      </div>`
    : '';

  const questionRows = qs
    .map((q) => {
      const r = rate(q);
      const mistake = full ? commonMistake(report, q) : null;
      return `<tr>
        <td class="num">${q.index + 1}</td>
        <td>${esc(q.title)}<div class="muted small">${TYPE_LABEL[q.type]}${q.pointsMultiplier === 2 ? ' · puntos dobles' : q.pointsMultiplier === 0 ? ' · sin puntos' : ''}</div></td>
        <td>${esc(q.correctAnswer)}</td>
        <td><div class="bar"><span style="width:${Math.round(r * 100)}%"></span></div><div class="small">${q.correctCount}/${q.totalPlayers} · ${pct(r)}</div></td>
        <td class="num">${q.answeredCount}/${q.totalPlayers}</td>
        <td class="num">${q.answeredCount ? seconds(q.avgResponseMs) + ' s' : '—'}</td>
        ${full ? `<td>${mistake ? `${esc(mistake.text)} <span class="muted">(${mistake.count})</span>` : '<span class="muted">—</span>'}</td>` : ''}
      </tr>`;
    })
    .join('');

  const matrix = full
    ? `<h2>Mapa de aciertos</h2>
      <div class="scroll"><table class="matrix">
        <thead><tr><th>#</th><th>Jugador</th><th>Puntos</th>${qs.map((q) => `<th class="c">P${q.index + 1}</th>`).join('')}<th class="num">Precisión</th></tr></thead>
        <tbody>${players
          .map(
            (p) => `<tr><td class="num">${p.placement}</td><td>${esc(p.displayName)}</td><td class="num">${p.score.toLocaleString('es')}</td>${p.answers
              .map((a) => `<td class="c">${statusCell(a)}</td>`)
              .join('')}<td class="num">${pct(p.accuracy)}</td></tr>`,
          )
          .join('')}</tbody>
      </table></div>`
    : '';

  const title = full ? 'Informe de la partida' : `Resultado de ${players[0]?.displayName ?? ''}`;

  return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)} · ${esc(report.quizTitle)}</title>
<style>
  :root{--fg:#1b1d24;--muted:#6b7080;--line:#e3e5ea;--bg:#fff;--soft:#f5f6f8;--ok:#1f9d55;--bad:#d64545;--part:#d98a1c;--accent:#e8702a}
  *{box-sizing:border-box}
  body{font:14px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:var(--fg);background:var(--bg);margin:0;padding:24px;max-width:1100px;margin-inline:auto}
  h1{margin:0 0 4px;font-size:24px} h2{font-size:17px;margin:32px 0 10px} h3{font-size:16px;margin:0 0 10px}
  .meta{color:var(--muted);margin-bottom:20px}
  .kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px;margin-bottom:14px}
  .kpi{background:var(--soft);border:1px solid var(--line);border-radius:10px;padding:8px 10px}
  .kl{color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.04em}.kv{font-weight:700;font-size:15px;word-break:break-word}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th,td{border-bottom:1px solid var(--line);padding:6px 8px;text-align:left;vertical-align:top}
  th{background:var(--soft);font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--muted)}
  .num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}.c{text-align:center}
  .muted{color:var(--muted)}.small{font-size:11px}
  .tag{display:inline-block;min-width:22px;padding:1px 6px;border-radius:6px;font-weight:700;font-size:12px}
  .tag.ok{background:#e3f5ea;color:var(--ok)}.tag.bad{background:#fbe6e6;color:var(--bad)}.tag.part{background:#fcf0dc;color:var(--part)}.tag.none{color:var(--muted)}
  .row-bad td{background:#fdf4f4}.row-part td{background:#fefaf1}.row-none td{color:var(--muted)}
  .bar{height:6px;background:var(--line);border-radius:3px;overflow:hidden;margin-bottom:2px;min-width:80px}.bar span{display:block;height:100%;background:var(--accent)}
  .player{border:1px solid var(--line);border-radius:12px;padding:16px;margin:16px 0;break-inside:avoid-page}
  .place{color:var(--accent)} .scroll{overflow-x:auto}
  .matrix td,.matrix th{padding:4px 6px}
  footer{margin-top:32px;color:var(--muted);font-size:12px}
  @media print{body{padding:0}.player{break-inside:avoid}}
</style></head><body>
<h1>${esc(title)}</h1>
<div class="meta">${esc(report.quizTitle)} · PIN ${esc(report.pin)} · ${esc(date)}</div>
${summary}
${full ? '' : playerSection(report, players[0])}
<h2>Preguntas</h2>
<div class="scroll"><table>
  <thead><tr><th>#</th><th>Pregunta</th><th>Respuesta correcta</th><th>Acierto de la sala</th><th class="num">Respondieron</th><th class="num">Tiempo medio</th>${full ? '<th>Error más común</th>' : ''}</tr></thead>
  <tbody>${questionRows}</tbody>
</table></div>
${matrix}
${full ? `<h2>Detalle por jugador</h2>${players.map((p) => playerSection(report, p)).join('')}` : ''}
<footer>Generado por ${esc(BRAND.name)}</footer>
</body></html>`;
}
